import React from 'react';
import { mutate as swrMutate } from 'swr';
import GenericModulePage from '../../components/generic/GenericModulePage';
import { initializeModule } from '../../utils/configInit';
import { riskAssessmentConfig } from './riskAssessmentConfig';
import { createRiskAssessmentValidationSchema } from './riskAssessmentValidation';
import { useCreateEntity, useUpdateEntity } from '../../hooks/useSWR';
import { API_BASE_URL } from '../../config/apiConfig';

/**
 * Initialize the RiskAssessment module using generic utilities
 */
const { config: initializedConfig } = initializeModule(
  riskAssessmentConfig,
  createRiskAssessmentValidationSchema
);

/**
 * Custom RiskAssessmentPage component with custom onSave handler for actions
 */
const RiskAssessmentPage = () => {
  // Get hooks for data operations
  const createEntity = useCreateEntity(initializedConfig.api.baseEndpoint);
  const updateEntity = useUpdateEntity(initializedConfig.api.baseEndpoint);
  
  const customOnSave = async (formData, formMode, currentData, setData) => {
    console.log('🚀 customOnSave CALLED with formData keys:', Object.keys(formData));
    console.log('🚀 customOnSave - formData.actions:', formData.actions);
    
    try {
      // Extract actions from formData before any transformations
      let actions = formData.actions;
      
      // If actions is undefined or null, try to get it from the sync function
      if (!actions && typeof window !== 'undefined' && window.__getRiskAssessmentActions) {
        const syncData = window.__getRiskAssessmentActions();
        if (syncData && syncData.localActions && Array.isArray(syncData.localActions)) {
          console.log('🔄 Got actions from sync function:', syncData.localActions);
          actions = syncData.localActions;
        }
      }
      
      console.log('🔍 STEP 1 - Raw actions from formData:', actions);
      console.log('🔍 STEP 1 - Is array?', Array.isArray(actions));
      
      // Ensure it's an array
      if (!Array.isArray(actions)) {
        console.warn('⚠️ actions is not an array, converting to empty array');
        actions = [];
      }
      
      // Filter out any invalid entries (must have at least description)
      actions = actions
        .filter(action => {
          const isValid = action && 
            action.description && 
            typeof action.description === 'string' &&
            action.description.trim() !== '';
          if (!isValid) {
            console.warn('⚠️ Filtering out invalid action:', action);
          }
          return isValid;
        })
        .map(action => ({
          description: action.description.trim(),
          startedAt: action.startedAt || null,
          dueDate: action.dueDate || null,
          closedAt: action.closedAt || null
        }));
      
      console.log('🔍 STEP 3 - After filtering and mapping - actions:', actions);
      console.log('🔍 STEP 3 - After filtering and mapping - actions length:', actions.length);
      
      // Replicate the full handleSave logic from GenericModulePage
      // STEP 1: Validate data
      if (initializedConfig.validateData && typeof initializedConfig.validateData === 'function') {
        const validationResult = await initializedConfig.validateData(formData);
        if (!validationResult.isValid) {
          const errorMessage = Object.values(validationResult.errors).join(', ');
          throw new Error(`Validation failed: ${errorMessage}`);
        }
      }
      
      // STEP 2: Clean data (replicate GenericModulePage logic)
      const systemFieldsToRemove = ['id', 'createdAt', 'updatedAt', '_count'];
      const fieldsToKeep = initializedConfig.fieldsToKeep || [];
      const filteredSystemFieldsToRemove = systemFieldsToRemove.filter(field => !fieldsToKeep.includes(field));
      
      // Get lookup endpoints for relation fields
      const lookupEndpoints = initializedConfig.api?.lookupEndpoints || {};
      const relationFieldsToRemove = [];
      Object.keys(lookupEndpoints).forEach(key => {
        const fieldName = key.replace(/s$/, '');
        relationFieldsToRemove.push(fieldName);
      });
      
      const fieldsToRemove = [
        ...filteredSystemFieldsToRemove, 
        ...relationFieldsToRemove, 
        ...(initializedConfig.fieldsToRemove || [])
      ];
      
      // Clean formData - remove system fields and relation objects
      const cleanedData = { ...formData };
      fieldsToRemove.forEach(field => {
        delete cleanedData[field];
      });
      
      // STEP 3: Transform data for backend
      // Only send multiselect fields that match the selected type
      const responsibleForActionType = formData.responsibleForActionType || null;
      
      const transformedData = {
        ...cleanedData,
        // Include actions array
        actions: actions.length > 0 ? actions : undefined,
        // Handle multiselect fields
        involvedDepartmentsIds: Array.isArray(formData.involvedDepartmentsIds) 
          ? formData.involvedDepartmentsIds 
          : (formData.involvedDepartmentsIds ? [formData.involvedDepartmentsIds] : []),
        responsibleForActionType: responsibleForActionType,
        // Only send the multiselect field that matches the selected type
        // Send undefined (not empty array) for fields that don't match the type
        responsibleForActionUserIds: responsibleForActionType === 'USER' 
          ? (Array.isArray(formData.responsibleForActionUserIds) 
              ? formData.responsibleForActionUserIds 
              : (formData.responsibleForActionUserIds ? [formData.responsibleForActionUserIds] : []))
          : undefined,
        responsibleForActionRoleIds: responsibleForActionType === 'ROLE'
          ? (Array.isArray(formData.responsibleForActionRoleIds) 
              ? formData.responsibleForActionRoleIds 
              : (formData.responsibleForActionRoleIds ? [formData.responsibleForActionRoleIds] : []))
          : undefined,
        responsibleForActionDepartmentIds: responsibleForActionType === 'DEPARTMENT'
          ? (Array.isArray(formData.responsibleForActionDepartmentIds) 
              ? formData.responsibleForActionDepartmentIds 
              : (formData.responsibleForActionDepartmentIds ? [formData.responsibleForActionDepartmentIds] : []))
          : undefined,
      };
      
      console.log('🔍 STEP 4 - Transformed data for backend:', transformedData);
      console.log('🔍 STEP 4 - Actions in transformed data:', transformedData.actions);
      
      // STEP 4: Save data
      let savedData;
      if (formMode === 'create') {
        savedData = await createEntity(transformedData);
      } else if (formMode === 'edit' && currentData?.id) {
        savedData = await updateEntity(currentData.id, transformedData);
      } else {
        throw new Error('Invalid form mode or missing ID for update');
      }
      
      console.log('✅ Save successful:', savedData);
      
      // STEP 5: Invalidate SWR cache
      await swrMutate(initializedConfig.api.baseEndpoint);
      
      return savedData;
    } catch (error) {
      console.error('❌ Error in customOnSave:', error);
      throw error;
    }
  };
  
  // Custom duplicate handler - since backend doesn't have duplicate endpoint
  const handleDuplicate = async (riskAssessmentId) => {
    try {
      const token = localStorage.getItem('authToken');
      const userId = localStorage.getItem('userId');
      
      // Fetch the full risk assessment with all relations
      const response = await fetch(`${API_BASE_URL}/risk-assessment/${riskAssessmentId}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(userId ? { 'user-id': userId } : {})
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch risk assessment');
      }

      const originalRiskAssessment = await response.json();
      
      // Extract actions for duplication (without closed dates)
      const actionsToDuplicate = (originalRiskAssessment.riskAssessmentActions || [])
        .filter(action => action.description && action.description.trim() !== '')
        .map(action => ({
          description: action.description.trim(),
          startedAt: action.startedAt || null,
          dueDate: action.dueDate || null,
          closedAt: null // Don't copy closed dates
        }));

      // Prepare duplicate data - use same logic as form save
      // Add "-COPY" suffix to the code
      const originalCode = originalRiskAssessment.code || '';
      const duplicatedCode = originalCode ? `${originalCode}-COPY` : '';
      
      const duplicateData = {
        ...originalRiskAssessment,
        // Add "-COPY" suffix to code
        code: duplicatedCode,
        // Prepare actions for duplication
        actions: actionsToDuplicate.length > 0 ? actionsToDuplicate : undefined,
      };

      // Use the same data cleaning logic as customOnSave
      const systemFieldsToRemove = ['id', 'createdAt', 'updatedAt', '_count'];
      const fieldsToKeep = initializedConfig.fieldsToKeep || [];
      const filteredSystemFieldsToRemove = systemFieldsToRemove.filter(field => !fieldsToKeep.includes(field));
      
      // Get lookup endpoints for relation fields
      const lookupEndpoints = initializedConfig.api?.lookupEndpoints || {};
      const relationFieldsToRemove = [];
      Object.keys(lookupEndpoints).forEach(key => {
        const fieldName = key.replace(/s$/, '');
        relationFieldsToRemove.push(fieldName);
      });
      
      // Additional fields to remove that shouldn't be sent to create endpoint
      const additionalFieldsToRemove = [
        'approvalRequests', // Approval requests should not be duplicated
        'riskAssessmentTimeLineEntries', // Timeline entries should not be duplicated
        'fileAttachments', // File attachments should not be duplicated
        'fileAttachmentCount', // File count should not be duplicated
        'requestedDate', // Requested date should not be duplicated
        'receivedDate', // Received date should not be duplicated
        'closedAt', // Closed date should not be duplicated
        'createdById', // Created by ID should not be duplicated
        'updatedById', // Updated by ID should not be duplicated
      ];
      
      const fieldsToRemove = [
        ...filteredSystemFieldsToRemove, 
        ...relationFieldsToRemove, 
        ...(initializedConfig.fieldsToRemove || []),
        ...additionalFieldsToRemove
      ];
      
      // Clean duplicateData - remove system fields and relation objects
      fieldsToRemove.forEach(field => {
        delete duplicateData[field];
      });

      // Remove object fields (likely relations from API) - but preserve arrays
      Object.keys(duplicateData).forEach(key => {
        if (duplicateData[key] && 
            typeof duplicateData[key] === 'object' && 
            !Array.isArray(duplicateData[key]) && 
            duplicateData[key].constructor === Object) {
          delete duplicateData[key];
        }
      });
      
      // Explicitly remove approvalRequests and other fields that might have been missed
      delete duplicateData.approvalRequests;
      delete duplicateData.riskAssessmentTimeLineEntries;
      delete duplicateData.fileAttachments;
      delete duplicateData.fileAttachmentCount;
      delete duplicateData.requestedDate;
      delete duplicateData.receivedDate;
      delete duplicateData.closedAt;

      // Handle responsible for action fields - convert arrays to ID arrays
      const responsibleForActionType = originalRiskAssessment.responsibleForActionType || null;
      
      // Convert relation arrays to ID arrays
      duplicateData.responsibleForActionUserIds = originalRiskAssessment.responsibleForActionUsers?.map(u => u.id) || [];
      duplicateData.responsibleForActionRoleIds = originalRiskAssessment.responsibleForActionRoles?.map(r => r.id) || [];
      duplicateData.responsibleForActionDepartmentIds = originalRiskAssessment.responsibleForActionDepartments?.map(d => d.id) || [];
      duplicateData.involvedDepartmentsIds = originalRiskAssessment.involvedDepartments?.map(d => d.id) || [];
      duplicateData.responsibleForActionType = responsibleForActionType;

      // Use transformData if available (same as save handler)
      const transformedData = initializedConfig.transformData
        ? initializedConfig.transformData(duplicateData, 'create')
        : duplicateData;

      // Create the duplicate using the create endpoint
      const duplicatedRiskAssessment = await createEntity(transformedData);
      
      // Refresh the data
      await swrMutate(initializedConfig.api.baseEndpoint);
      
      return duplicatedRiskAssessment;
    } catch (error) {
      console.error('Error duplicating risk assessment:', error);
      throw error;
    }
  };

  // Pass custom handlers to config
  const configWithCustomHandlers = {
    ...initializedConfig,
    customHandlers: {
      ...initializedConfig.customHandlers,
      onDuplicate: handleDuplicate
    }
  };

  return (
    <GenericModulePage
      config={configWithCustomHandlers}
      customOnSave={customOnSave}
    />
  );
};

export default RiskAssessmentPage;
